export type LayerKey = 'point_limite' | 'pfp' | 'pfa' | 'parcelle'
export type BasemapKey = 'osm' | 'plan_sitg' | 'ortho_2024'

export const LAYERS: Record<
  LayerKey,
  { label: string; color: string; description: string; labelField: string; type: 'point' | 'polygon' }
> = {
  point_limite: {
    label: 'Points limites',
    color: '#4b5563', // gris foncé (rouge si valeur = 6)
    description: 'Bornes et chevilles matérialisant les limites de biens-fonds',
    labelField: 'no_point',
    type: 'point',
  },
  pfp: {
    label: 'Points fixes planimétriques (cat. 1, 2 & 3)',
    color: '#f87171', // rouge clair
    description: 'PFP cat. 1 & 2 (triangulation) + cat. 3 (polygonation)',
    labelField: 'numero',
    type: 'point',
  },
  pfa: {
    label: 'Points fixes altimétriques',
    color: '#60a5fa', // bleu clair
    description: 'PFA1 / PFA2 — réseau national de nivellement',
    labelField: 'numero',
    type: 'point',
  },
  parcelle: {
    label: 'Parcelles cadastrales',
    color: '#f97316',
    description: 'Parcelles de la mensuration officielle (biens-fonds)',
    labelField: 'no_parcelle',
    type: 'polygon',
  },
}

export const BASEMAPS: Record<BasemapKey, { label: string; description: string }> = {
  osm: {
    label: 'OpenStreetMap',
    description: 'Fond de carte mondial libre',
  },
  plan_sitg: {
    label: 'Plan SITG',
    description: 'Plan officiel du canton de Genève (SITG)',
  },
  ortho_2024: {
    label: 'Orthophoto 2024',
    description: 'Photo aérienne SITG mars 2024',
  },
}

interface Props {
  active: Set<LayerKey>
  onToggle: (key: LayerKey) => void
  basemap: BasemapKey
  onBasemapChange: (key: BasemapKey) => void
  showLabels: boolean
  onToggleLabels: (v: boolean) => void
}

export default function LayerControl({
  active,
  onToggle,
  basemap,
  onBasemapChange,
  showLabels,
  onToggleLabels,
}: Props) {
  return (
    <>
      <h2>Fond de carte</h2>
      {(Object.keys(BASEMAPS) as BasemapKey[]).map((key) => (
        <label key={key} className="basemap-toggle" title={BASEMAPS[key].description}>
          <input
            type="radio"
            name="basemap"
            checked={basemap === key}
            onChange={() => onBasemapChange(key)}
          />
          <span>{BASEMAPS[key].label}</span>
        </label>
      ))}

      <h2 style={{ marginTop: '1.25rem' }}>Couches MO</h2>
      {(Object.keys(LAYERS) as LayerKey[]).map((key) => (
        <label key={key} className="layer-toggle" title={LAYERS[key].description}>
          <input
            type="checkbox"
            checked={active.has(key)}
            onChange={() => onToggle(key)}
          />
          <span
            className="layer-color"
            style={{
              background: LAYERS[key].color,
              ...(LAYERS[key].type === 'polygon'
                ? { borderRadius: '2px', width: '16px', height: '10px' }
                : {}),
            }}
          />
          <span>{LAYERS[key].label}</span>
        </label>
      ))}

      <h2 style={{ marginTop: '1.25rem' }}>Affichage</h2>
      <label className="layer-toggle">
        <input
          type="checkbox"
          checked={showLabels}
          onChange={(e) => onToggleLabels(e.target.checked)}
        />
        <span>Étiquettes (n° de points, zoom ≥ 17)</span>
      </label>

      <p style={{ marginTop: '1.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
        Données : SITG — État de Genève. Chargement dynamique à partir du zoom 15.
      </p>
      <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
        Points limites : gris foncé,{' '}
        <span style={{ color: '#dc2626', fontWeight: 600 }}>rouge</span> si non matérialisé
        (valeur = 6).
      </p>
    </>
  )
}
